import { Trans } from "react-i18next";
import { useState } from "react";
import { BROWSER_RELEASE_NOTES } from "@shared/config/browserSupport";
import {
  BrowserFamily,
  BrowserInfo,
  BrowserSupportEvaluation,
  getBrowserDisplayName,
} from "@shared/utils/browserDetection";
import "./BrowserSupportGate.css";

export type BrowserGateBranding = {
  orgName: string;
  orgSlug?: string | null;
  logoUrl?: string | null;
  source: "default" | "tenant" | "slug_fallback";
};

type BrowserSupportGateProps = {
  browser: BrowserInfo;
  evaluation: BrowserSupportEvaluation;
  branding: BrowserGateBranding;
  supportedFamilies: BrowserFamily[];
  onContinue?: () => void;
  onOverride?: () => void;
  allowOverride?: boolean;
  overrideTtlMinutes?: number;
};

function formatSupportedBrowsers(families: BrowserFamily[]): string {
  const names = families.map((family) => getBrowserDisplayName(family));
  if (names.length === 0) {
    return "supported browsers";
  }
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function getDownloadLabel(family: BrowserFamily): string {
  switch (family) {
    case "chrome":
      return "Download Chrome";
    case "edge":
      return "Download Edge";
    case "firefox":
      return "Download Firefox";
    default:
      return `Download ${getBrowserDisplayName(family)}`;
  }
}

function formatDateLabel(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function buildUpgradeGuidance(
  evaluation: BrowserSupportEvaluation,
  supportedFamilies: BrowserFamily[],
): string {
  if (evaluation.reason === "family_not_allowed") {
    return `This application supports current versions of ${formatSupportedBrowsers(supportedFamilies)}.`;
  }
  if (evaluation.reason === "unknown_version") {
    return "Your browser version could not be verified. Update to the latest stable version and retry.";
  }
  if (evaluation.reason === "version_too_low" && evaluation.minimumVersion != null) {
    return `Your browser is out of date. Minimum required major version is ${evaluation.minimumVersion}.`;
  }
  if (
    evaluation.reason === "version_in_warning_window" &&
    evaluation.warningVersion != null
  ) {
    return `Your browser will soon be unsupported. Upgrade to major version ${evaluation.warningVersion} or later.`;
  }
  return "Your browser is currently not supported for this login surface.";
}

export default function BrowserSupportGate({
  browser,
  evaluation,
  branding,
  supportedFamilies,
  onContinue,
  onOverride,
  allowOverride = true,
  overrideTtlMinutes,
}: BrowserSupportGateProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const enforcementDateLabel = formatDateLabel(evaluation.enforcementDate);
  const isWarning = evaluation.state === "warning";
  const isBlocked = evaluation.state === "blocked";
  const downloadTargets = supportedFamilies
    .map((family) => ({ family, href: BROWSER_RELEASE_NOTES[family] }))
    .filter(
      (entry): entry is { family: BrowserFamily; href: string } =>
        typeof entry.href === "string" && entry.href.length > 0,
    );

  return (
    <div
      className={`browser-gate-shell ${isWarning ? "browser-gate-shell-warning" : ""}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="browser-gate-card">
        <div className="browser-gate-brand">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={`${branding.orgName} logo`}
              className="browser-gate-brand-logo"
            />
          ) : (
            <div className="browser-gate-brand-fallback">
              <i className="bi bi-shield-check"></i>
            </div>
          )}
          <div className="browser-gate-brand-text">
            <p className="browser-gate-brand-kicker"><Trans ns="features" i18nKey="ui.copy.e8f08218484d" /></p>
            <strong>{branding.orgName}</strong>
          </div>
        </div>

        <h1>{isWarning ? "Browser Update Recommended" : "Browser Update Required"}</h1>
        <p className="browser-gate-copy">
          {buildUpgradeGuidance(evaluation, supportedFamilies)}
        </p>
        {enforcementDateLabel && (
          <p className="browser-gate-cutoff">
            <Trans ns="features" i18nKey="ui.copy.7ac1230db054" /><strong>{enforcementDateLabel}</strong>
          </p>
        )}
        <dl className="browser-gate-meta">
          <div>
            <dt><Trans ns="features" i18nKey="ui.copy.b6cf7500b422" /></dt>
            <dd>{getBrowserDisplayName(browser.family)}</dd>
          </div>
          <div>
            <dt><Trans ns="features" i18nKey="ui.copy.70a33a78fa90" /></dt>
            <dd>{browser.major ?? "Unknown"}</dd>
          </div>
        </dl>

        <div className="browser-gate-actions">
          {downloadTargets.map(({ family, href }) => (
            <a
              key={family}
              className="browser-gate-link"
              href={href}
              target="_blank"
              rel="noreferrer"
            >
              {getDownloadLabel(family)}
            </a>
          ))}
        </div>

        {isWarning && onContinue && (
          <button
            type="button"
            className="browser-gate-continue"
            onClick={onContinue}
          >
            <Trans ns="features" i18nKey="ui.copy.96471fae706c" /></button>
        )}

        {isBlocked && allowOverride && onOverride && (
          <>
            <label className="browser-gate-ack">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              <span>
                <Trans ns="features" i18nKey="ui.copy.9f8396b9f7c6" /></span>
            </label>
            <button
              type="button"
              className="browser-gate-override"
              onClick={onOverride}
              disabled={!acknowledged}
            >
              <Trans ns="features" i18nKey="ui.copy.0aaa89d883d4" /></button>
            {overrideTtlMinutes != null && (
              <p className="browser-gate-ttl">
                <Trans ns="features" i18nKey="ui.copy.75a6997ae15e" />{overrideTtlMinutes} <Trans ns="features" i18nKey="ui.copy.813230097913" /></p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
